import DesignTokens from '@tableau/design-tokens';
import { FieldRenameIcon } from '@tableau/icons/FieldRenameIcon';
import { PlayCircleIcon } from '@tableau/icons/PlayCircleIcon';
import { ButtonType, ButtonWidget, InteractionState } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';

import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { IEntityAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/IEntityAction';
import { RenameScheduleAction } from '~src/spacesPlatform/components/ActionsMenu/EntityActions/RenameEntityAction';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { applyColor } from '~src/style/ColorService';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { ISpaceItemDetailInfo } from '../../spacesPlatform/models/ISpaceItemDetailInfo';
import { ScheduleRunNowAction } from '../actions/ScheduleRunNowAction';
import { IScheduleInfo } from '../types/IScheduleInfo';


export interface ScheduleSpaceHeaderProps {
  readonly item: ISpaceItemDetailInfo<'schedule'>;
  readonly displaySchedulesInClientTimezone: boolean;
}

export function ScheduleSpaceHeader(props: ScheduleSpaceHeaderProps): JSX.Element {
  return <ConnectedSpaceHeader
      item={props.item}
      detailIcons={
        <React.Fragment>
          { RunNowButton(props.item) }
          { RenameButton(props.item) }
        </React.Fragment>
      }
      metadataElement={
        <React.Fragment>
          <MetaDataRow {...props} />
        </React.Fragment>
      }
  />;
}
//test
export function MetaDataRow(props: ScheduleSpaceHeaderProps) {
  const schedule = props.item;
  const description = IScheduleInfo.getScheduleDescription(schedule);
  return (
    <h2>
      <span className={SpaceHeaderStyles.ScheduleLabel}>
        {Messages.schedule()}
      </span>
      <span className={SpaceHeaderStyles.MetadataInfo}>
        { description }
      </span>
      { props.item.runNextAt &&
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.runNextAt}>
          {Messages.nextRunAt({NEXT_RUN: L10n.localizedDateTime(props.item.runNextAt)})}
        </span>
      }
    </h2>
  );
}

interface NameRowButtonOwnProps {
  readonly iconPath?: string;
  readonly iconActivePath?: string;
  readonly action: IEntityAction<IScheduleInfo, {}>;
  readonly item: ISpaceItemDetailInfo<'schedule'>;
  readonly altText: string;
  readonly getContentOverride?: (state: InteractionState) => JSX.Element;
}

interface NameRowButtonStateProps {
  readonly isVisible: boolean;
}

interface NameRowButtonDispatchProps {
  readonly onClick: () => void;
}

type NameRowButtonProps = NameRowButtonOwnProps & NameRowButtonStateProps & NameRowButtonDispatchProps;

function NameRowButton(props: NameRowButtonProps) {
  if (!props.isVisible) {
    return null;
  }

  return (
    <ButtonWidget
      buttonType={ButtonType.LowEmphasis}
      getContent={props.getContentOverride ?? getDetailButtonContent(props.iconPath!, props.iconActivePath!, props.altText)}
      testId={props.action.name}
      handleClick={props.onClick}
      style={FeatureFlags.compactHeaderEnabled ? SpaceHeaderStyles.IconButtonStyleOverridesV2 : SpaceHeaderStyles.IconButtonStyleOverrides}
    />
  );
}

const mapStateToProps: MapStateToProps<NameRowButtonStateProps> = (state: AppState, ownProps: NameRowButtonOwnProps) => ({
  isVisible: ownProps.action.isClickable({}, [ownProps.item], state) && ownProps.action.isVisible([ownProps.item], state)
});

const mapDispatchToProps: MapDispatchToProps<NameRowButtonDispatchProps> = (dispatch, ownProps: NameRowButtonOwnProps) => {
  return {
    onClick: () => {
      dispatch(ownProps.action.createReduxAction([ownProps.item]));
    }
  };
};

export const ConnectedNameRowButton: React.ComponentClass<NameRowButtonOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(NameRowButton);

const getDetailButtonContent = (iconPath: string, activePath: string, altText: string): (state: InteractionState) => JSX.Element => {
  return (state: InteractionState): JSX.Element => {
    const showActiveIcon = state.hover || state.active || state.focus;
    /* eslint-disable jsx-a11y/alt-text */
    return (
      <img
        className={SpaceHeaderStyles.HeaderButtonImage}
        src={showActiveIcon ? activePath : iconPath }
        alt={ altText }
      />
    );
  };
};

const getRunNowButtonContent = (altText: string): ((state: InteractionState) => JSX.Element) => {
  return (state: InteractionState): JSX.Element => {
    const showActive = state.hover || state.active || state.focus;
    return (
      <PlayCircleIcon
        size={18}
        alt={altText}
        className={SpaceHeaderStyles.HeaderButtonImage}
        color={showActive
          ? applyColor(DesignTokens.color.font.default.default, '#333333')
          : applyColor(DesignTokens.color.font.default.secondary, '#666666')}
      />
    );
  };
};

const RunNowButton = (item: ISpaceItemDetailInfo<'schedule'>) => {
  return <ConnectedNameRowButton action={ScheduleRunNowAction} getContentOverride={getRunNowButtonContent(Messages.runNow())} item={item} altText={Messages.runNow()}/>;
};

const getRenameButtonContent = (altText: string): ((state: InteractionState) => JSX.Element) => {
  return (state: InteractionState): JSX.Element => {
    const showActive = state.hover || state.active || state.focus;
    return (
      <FieldRenameIcon
        size={18}
        alt={altText}
        className={SpaceHeaderStyles.HeaderButtonImage}
        color={showActive
          ? applyColor(DesignTokens.color.font.default.default, '#333333')
          : applyColor(DesignTokens.color.font.default.secondary, '#666666')}
      />
    );
  };
};

const RenameButton = (item: ISpaceItemDetailInfo<'schedule'>) => {
  return <ConnectedNameRowButton action={RenameScheduleAction} getContentOverride={getRenameButtonContent(Messages.rename())} item={item} altText={Messages.rename()}/>;
};
